import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import {
    CaseBrief,
    CaseBriefTableBlock,
    createEmptyCaseBrief,
    createTableBlock,
    createTextBlock,
    hasCaseBriefContent,
    parseCaseBrief,
    serializeCaseBrief
} from '@app/vinter/models/case-brief';

/**
 * Case Study kategorisi için vaka materyali editörü.
 * Metin (zengin metin) ve tablo blokları eklenir; sonuç JSON string olarak `valueChange` ile dışarı verilir.
 * Quill tablo desteklemediği için tablolar ayrı blok tipi olarak satır/sütun girişiyle tutulur.
 */
@Component({
    selector: 'app-case-brief-editor',
    templateUrl: './case-brief-editor.component.html',
    styleUrls: ['./case-brief-editor.component.scss']
})
export class CaseBriefEditorComponent implements OnInit, OnChanges {
    /** JSON string (QuestionCollection.CaseBrief). */
    @Input() value: string;
    @Input() disabled: boolean = false;
    @Output() valueChange: EventEmitter<string> = new EventEmitter<string>();
    /** En az bir dolu blok varsa true. */
    @Output() validityChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    brief: CaseBrief = createEmptyCaseBrief();
    // add-your-question ile aynı kısıtlı format seti.
    formats: string[] = ['bold', 'italic', 'underline', 'list'];

    private lastEmitted: string | null = null;

    ngOnInit(): void {
        this.brief = parseCaseBrief(this.value);
        this.emitValidity();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.value && !changes.value.firstChange && this.value !== this.lastEmitted) {
            this.brief = parseCaseBrief(this.value);
            this.emitValidity();
        }
    }

    get hasContent(): boolean {
        return hasCaseBriefContent(this.brief);
    }

    trackByIndex(index: number): number {
        return index;
    }

    addTextBlock(): void {
        this.brief.blocks.push(createTextBlock());
        this.emit();
    }

    addTableBlock(): void {
        this.brief.blocks.push(createTableBlock('', 2, 2));
        this.emit();
    }

    removeBlock(index: number): void {
        this.brief.blocks.splice(index, 1);
        this.emit();
    }

    moveBlock(index: number, direction: -1 | 1): void {
        const target = index + direction;
        if (target < 0 || target >= this.brief.blocks.length) {
            return;
        }
        const blocks = this.brief.blocks;
        [blocks[index], blocks[target]] = [blocks[target], blocks[index]];
        this.emit();
    }

    addColumn(block: CaseBriefTableBlock): void {
        block.columns.push('');
        block.rows.forEach(row => row.push(''));
        this.emit();
    }

    removeColumn(block: CaseBriefTableBlock, columnIndex: number): void {
        if (block.columns.length <= 1) {
            return;
        }
        block.columns.splice(columnIndex, 1);
        block.rows.forEach(row => row.splice(columnIndex, 1));
        this.emit();
    }

    addRow(block: CaseBriefTableBlock): void {
        block.rows.push(block.columns.map(() => ''));
        this.emit();
    }

    removeRow(block: CaseBriefTableBlock, rowIndex: number): void {
        block.rows.splice(rowIndex, 1);
        this.emit();
    }

    onChanged(): void {
        this.emit();
    }

    private emit(): void {
        this.lastEmitted = serializeCaseBrief(this.brief);
        this.valueChange.emit(this.lastEmitted);
        this.emitValidity();
    }

    private emitValidity(): void {
        this.validityChange.emit(this.hasContent);
    }
}
